// An address with its point (BL-018, the maps design v3, place 2 and package 6): on the job sheet,
// under Details, and on the customer record, under each address. It shows the address, a small Google
// map with its point, where the point came from in plain words, and Navigate in Google Maps.
//
// The point is the one jobs.get and customers.get give (location_points_preferred: ours first, then
// Google's while its 30 days last). A Google point goes on Google's map only: the stand-in leaves it off
// and says so (GoogleMap draws only our own points on it). Google's own credits stay on its map, as the
// terms require; we draw nothing over them. An address with no point has no map, says so in red, and Navigate goes by the
// address as written.
//
// `actions` is where "Move the pin" goes once its action is built (maps package 3); it sits beside
// Navigate, as the design draws it.
import type { ReactNode } from 'react';
import type { MapsLoader } from '../map-loader.ts';
import type { PointSource } from '../map-schemas.ts';
import type { MapTheme } from '../map-style.ts';
import { GoogleMap, type GoogleMapProps } from './GoogleMap/GoogleMap.tsx';
import { NavigateLink } from './NavigateLink.tsx';

/** A point as the office reads it: where it is, where it came from, and who set it. */
export interface AddressPointView {
  lat: number;
  lng: number;
  source: PointSource;
  /** The person who set it, by name (the office's pin, the technician who arrived). */
  setBy: string | null;
}

/** Where the point came from, in plain words; no point at all is "No pin yet". */
export function pointSourceText(point: AddressPointView | null): string {
  if (!point) return 'No pin yet';
  switch (point.source) {
    case 'imported':
      return 'Came with the imported address';
    case 'google':
      return 'Placed by Google';
    case 'office':
      return point.setBy ? `Pin set by ${point.setBy}` : 'Pin set by the office';
    case 'arrival':
      return point.setBy ? `Confirmed on arrival by ${point.setBy}` : 'Confirmed on arrival';
    case 'routing':
      return 'Placed by an address lookup';
  }
}

export interface AddressCheckProps {
  /** The address as written, on one line: "2214 Gorge Vale Pl, Saanich". */
  address: string;
  point: AddressPointView | null;
  /** Light or dark, as the page draws it now. */
  theme: MapTheme;
  /** The browser's key (the session's mapsKey); none draws the stand-in. */
  mapsKey?: string | null;
  /** Why there's no key (the session's mapsOff): the company's maps off says so (QA-756). */
  mapsOff?: GoogleMapProps['mapsOff'];
  /** With maps off, where they're turned on, for someone who may; everyone else is told an owner can. */
  turnOn?: GoogleMapProps['turnOn'];
  /** Off where the address is already written just above (the customer record's address rows). */
  showAddress?: boolean;
  /** Off for a long list of addresses: each map is a load Google counts, so the list keeps to words. */
  withMap?: boolean;
  /** Beside Navigate: where "Move the pin" goes once it's built. */
  actions?: ReactNode;
  /** The map's loader, for a test or the gallery. */
  loader?: MapsLoader | null;
}

export function AddressCheck(p: AddressCheckProps) {
  const { address, point } = p;
  return (
    <div className="flw-addr" role="group" aria-label={`Where ${address} is`}>
      {p.showAddress === false ? null : <b className="flw-addr-at">{address}</b>}
      {point && p.withMap !== false && (
        <GoogleMap
          data={{
            pins: [
              {
                id: 'here',
                lat: point.lat,
                lng: point.lng,
                label: '',
                title: address,
                area: null,
                state: 'place',
                late: false,
                pen: null,
                source: point.source,
              },
            ],
            routes: [],
            shop: null,
            techs: [],
          }}
          label={`${address} on the map`}
          theme={p.theme}
          size="small"
          legend={null}
          {...(p.mapsKey === undefined ? {} : { mapsKey: p.mapsKey })}
          {...(p.mapsOff === undefined ? {} : { mapsOff: p.mapsOff })}
          {...(p.turnOn === undefined ? {} : { turnOn: p.turnOn })}
          {...(p.loader === undefined ? {} : { loader: p.loader })}
        />
      )}
      <p className={`flw-addr-how${point ? '' : ' flw-bad'}`}>{pointSourceText(point)}</p>
      <div className="flw-addr-links">
        <NavigateLink address={address} point={point} />
        {p.actions}
      </div>
    </div>
  );
}
